import { MoreOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from "antd";

import deleteIcon from "assets/icons/delete.svg";
import { API } from "aws-amplify";
import { employeeByEmployer } from "graphql/customEpcQuery";
import { listDoctorCustom } from "graphql/customQuery";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import TableSkeleton from "utility/TableSkeleton";
import { doctorNameRegex, emailPattern } from "utility/constant";
import search from "utility/search";
import { callGraphQLBatch } from "utils/api";
import CommonCSVExport from "../../commonComponents/CSVExport";
import CommonSearch from "../../commonComponents/Search";
import CommonTable from "../../commonComponents/Table";
import { specialDoctorID } from "utils/general";

interface UserData {
  key: string;
  email: string;
  doctorName: string;
  firstName: string;
  lastName: string;
  Action: string;
  createdAt: string;
}
//accept id as a prop and its type is string
export interface EmployeesListingProps {
  id?: string;
}

export default function EmployeesListing({ id }: EmployeesListingProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [doctorList, setDoctorList] = useState<any[]>([]);
  const [dataSource, setDataSource] = useState<UserData[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();
  const [deleteModelPrompt, setDeleteModelPrompt] = useState(false);
  const [deleteId, setDeleteId] = useState<[string, string]>(["", ""]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const { id: employerId } = useParams();

  const location = useLocation();

  const [form] = Form.useForm();

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      width: 220,
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      width: 180,
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      width: 180,
    },
    {
      title: "Doctor Name",
      dataIndex: "doctorName",
      width: 180,
    },
    {
      title: "",
      dataIndex: "Action",
      className: "actions",
      width: 50,
      render: (id: string, record: UserData) => {
        return (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="1">
                  <Link
                    rel="noopener noreferrer"
                    to={`/employee-edit/${id}?employerId=${employerId}`}
                    state={{
                      id: [id],
                      member: undefined,
                      module: "Employer",
                      employerId: employerId,
                      from: "DPC",
                      tablePage: currentPage,
                      dpcDoctorID: null,
                      dpcBillingStatus: null,
                      pageSize: pageSize,
                    }}
                  >
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item key="1">
                  <Link
                    to={""}
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      setDeleteModelPrompt(true);
                      setDeleteId([id, record?.email]);
                    }}
                  >
                    Delete
                    {/* WIP */}
                  </Link>
                </Menu.Item>
              </Menu>
            }
            overlayClassName="download-btn"
            placement="bottomRight"
          >
            <MoreOutlined />
          </Dropdown>
        );
      },
    },
  ];

  const fetchUsersUnderEmployer = useCallback(async () => {
    try {
      setLoading(true);

      const variables = {
        epcEmployerID: id,
        sortDirection: "DESC",
      };
      const [users] = await callGraphQLBatch([
        {
          query: employeeByEmployer,
          authMode: "AMAZON_COGNITO_USER_POOLS",
          variables,
          outputKeyword: "employeeByEmployer",
        },
      ]);

      // Merge and map data
      const merged = [...(users || [])].map((item) => ({
        key: item?.id,
        email: item?.emailID,
        doctorName: item?.dpcDoctor?.doctorName || "-",
        firstName: item?.profile?.firstName
          ? item?.profile?.firstName
          : item?.firstName || "-",
        lastName: item?.profile?.lastName
          ? item?.profile?.lastName
          : item?.lastName || "-",
        Action: item?.id,
        createdAt: item?.createdAt,
      }));

      // Sort by createdAt descending
      merged.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setDataSource(merged);
      setUserData(merged);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsersUnderEmployer();
  }, [fetchUsersUnderEmployer]);

  const headers = [
    { label: "Member Email", key: "email" },
    { label: "First Name", key: "firstName" },
    { label: "Last Name", key: "lastName" },
    { label: "Doctor Name", key: "doctorName" },
  ];

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const currValue = e.target.value.trim();
    setSearchValue(e.target.value);
    const searchFields = ["email", "doctorName", "name", "role"];
    const filteredData = search(userData, currValue, searchFields);
    setDataSource(filteredData);
  };

  const addEmployee = async (values: FormData) => {
    setCreateLoading(true);
    try {
      const myInit = {
        body: { ...values, employerId: id },
      };

      const response = await API.post(
        "bazarhealthREST",
        "/epc/superadmin/addEmployee",
        myInit
      );

      messageApi.open({
        type: "success",
        content: response.message || "Employee created successfully!",
      });
      fetchUsersUnderEmployer();
    } catch (error: any) {
      messageApi.open({
        type: "error",
        content:
          error.response.data.message ||
          "Failed to create employee. Please try again.",
      });
    } finally {
      setIsModalOpen(false);
      form.resetFields();
      setCreateLoading(false);
    }
  };

  const deleteUser = async (id: string, email: string) => {
    try {
      setDeleteLoading(true);
      const myInit = {
        body: {
          id: id,
          email: email,
        },
      };
      await API.post(
        "bazarhealthREST",
        `/epc/superadmin/removeEmployee`,
        myInit
      );
      const newArray = dataSource.filter(function (obj) {
        return obj.key !== id;
      });
      setDeleteLoading(false);
      setDeleteModelPrompt(false);
      messageApi.open({
        key: "delete",
        type: "success",
        content: "Member Deleted Successfully",
      });
      setDeleteId(["", ""]);
      setDataSource(newArray);
      setUserData(newArray);
    } catch (error) {
      messageApi.open({
        key: "delete",
        type: "error",
        content: "Error Deleting Member",
      });
      setDeleteLoading(false);
      setDeleteModelPrompt(false);
    }
  };

  function getCsvExportData() {
    return dataSource.map((row) => {
      const {
        email,
        firstName = "-",
        lastName = "-",
        doctorName = "-",
      } = row || {};
      return {
        email,
        name: `${firstName} ${lastName}`,
        doctorName,
      };
    });
  }

  return (
    <>
      {contextHolder}
      <Row justify="space-between" className="table-header">
        <Row align="middle">
          <CommonSearch onChange={handleSearch} value={searchValue} />
        </Row>
        <Row className="gap-10 mtr-20 bold-font filter-dropdown-list">
          <CommonCSVExport
            data={getCsvExportData()}
            headers={headers}
            filename={`All_Employees.csv`}
            onClick={() =>
              messageApi.open({
                type: "loading",
                content: "Exporting CSV...",
                duration: 1,
              })
            }
          />
          <Button
            className="blue-btn inherit-btn"
            onClick={async () => {
              if (doctorList.length === 0) {
                const [response] = await callGraphQLBatch([
                  {
                    query: listDoctorCustom,
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                    variables: {
                      filter: {
                        isDpcDoctor: { eq: true },
                        activeOnPortal: { eq: true },
                      },
                      limit: "9999",
                    },
                    outputKeyword: "listDoctorProfiles",
                  },
                ]);
                if (response?.length > 0) {
                  setDoctorList(response);
                } else {
                  messageApi.open({
                    type: "warning",
                    content: "No Medical Provider found",
                  });
                  return;
                }
              }
              setIsModalOpen(true);
            }}
          >
            Add Member
          </Button>
        </Row>{" "}
      </Row>

      {loading ? (
        <TableSkeleton />
      ) : (
        <CommonTable
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          className="common-table"
          scroll={{ x: 992 }}
          pagination={{
            position: ["bottomCenter"],
            defaultPageSize: 100,
          }}
          onChange={(pagination) => {
            setCurrentPage(pagination.current);
            setPageSize(pagination.pageSize);
          }}
        />
      )}

      <Modal
        className="form-modal"
        centered
        footer={null}
        width={700}
        title={
          <Typography.Title level={3} className="modal-title">
            Add Member
          </Typography.Title>
        }
        visible={isModalOpen}
        onOk={() => setIsModalOpen(false)}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
      >
        <Form
          name="basic"
          onFinish={addEmployee}
          autoComplete="off"
          layout="vertical"
          form={form}
        >
          <Row gutter={20}>
            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[
                  {
                    required: true,
                    message: "Please enter First Name",
                  },
                  {
                    pattern: doctorNameRegex,
                    message: "Please enter a valid First Name",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[
                  {
                    required: true,
                    message: "Please enter Last Name",
                  },
                  {
                    pattern: doctorNameRegex,
                    message: "Please enter a valid Last Name",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Email ID"
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please enter email id",
                  },
                  {
                    pattern: emailPattern,
                    message: "Please provide a valid Email!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12} md={12} xs={24} sm={24}>
              <Form.Item
                label="Choose Your Medical Provider"
                name="doctorId"
                rules={[
                  {
                    required: true,
                    message: "Please select any employer",
                  },
                ]}
              >
                <Select allowClear placeholder="Select your provider">
                  {/*
                    Temporary logic for handling the special doctor (Dr. Boucher) until December 1st:
                    1. By default, show all doctors except the one with `specialDoctorID` (Dr. Boucher).
                    2. If the query parameter contains `specialDoctorID`, then only show that doctor.
                    TODO: Remove this conditional logic after December 1st.
                  */}
                  {/* {doctorList.map((item) => (
                    <Select.Option
                      key={item?.id}
                      value={item?.id}
                    >{`${item.doctorName}`}</Select.Option>
                  ))} */}
                  {doctorList.map((item) =>
                    item.id === specialDoctorID ? null : (
                      <Select.Option key={item?.id} value={item?.id}>
                        {item?.doctorName}
                      </Select.Option>
                    )
                  )}
                </Select>
              </Form.Item>
            </Col>

            <Col
              span={12}
              md={24}
              xs={24}
              sm={24}
              style={{ textAlign: "center" }}
            >
              <Button
                className="theme-btn"
                htmlType="submit"
                loading={createLoading}
              >
                Add Member
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Basic Modal"
        open={deleteModelPrompt}
        onOk={() => setDeleteModelPrompt(false)}
        centered
        onCancel={() => setDeleteModelPrompt(false)}
        className="reward-success-modal "
        footer={null}
      >
        <img src={deleteIcon} alt="Delete Img" />
        <h6>Are you sure ?</h6>
        <p>If Yes, it will delete an member permanently.</p>

        <Space>
          <Button
            type="primary"
            className="theme-btn"
            style={{ width: "106px" }}
            onClick={() => deleteUser(deleteId[0], deleteId[1])}
            loading={deleteLoading}
          >
            Yes
          </Button>
          <Button
            type="primary"
            className="theme-btn "
            onClick={() => {
              setDeleteModelPrompt(false);
              setDeleteId(["", ""]);
            }}
            style={{ width: "106px" }}
          >
            Cancel
          </Button>
        </Space>
      </Modal>
    </>
  );
}
